import { useMemo } from "react";
import { useBodyPalette } from "../../scene/BodyFinish";
import { GLASS, HEADLIGHT, TAIL_LIGHT } from "../../scene/materials";
import { glassShellProfile, glasshouseFrameProfile, lowerBodyProfile } from "./profiles";
import { CAR, FRONT_AXLE_X, NOSE_X, REAR_AXLE_X } from "./dimensions";
import { buildShell } from "../geometry/shell";
const BODY_EXTRUDE = {
  depth: CAR.width - 0.1,
  bevelEnabled: true,
  bevelThickness: 0.05,
  bevelSize: 0.05,
  bevelSegments: 5,
  curveSegments: 32,
  steps: 1
};
const GLASS_EXTRUDE = {
  depth: 1.44,
  bevelEnabled: true,
  bevelThickness: 0.02,
  bevelSize: 0.02,
  bevelSegments: 3,
  curveSegments: 32,
  steps: 1
};
const FRAME_EXTRUDE = {
  depth: 1.616,
  bevelEnabled: true,
  bevelThickness: 0.012,
  bevelSize: 0.012,
  bevelSegments: 3,
  curveSegments: 32,
  steps: 1
};
const BODY_TAPER = { halfLength: NOSE_X, nose: 0.86, tail: 0.9, hold: 0.68 };
const GLASSHOUSE_TAPER = { halfLength: 1.74, nose: 0.92, tail: 0.92, hold: 0.55 };
const B_PILLAR_Z = 0.785;
const MIRROR_Z = 0.9;
function CarBody() {
  const palette = useBodyPalette("car");
  const body = useMemo(
    () => buildShell(lowerBodyProfile(), BODY_EXTRUDE, {
      depth: BODY_EXTRUDE.depth,
      taper: BODY_TAPER
    }),
    []
  );
  const glass = useMemo(
    () => buildShell(glassShellProfile(), GLASS_EXTRUDE, {
      depth: GLASS_EXTRUDE.depth,
      taper: GLASSHOUSE_TAPER,
      passes: 1
    }),
    []
  );
  const frame = useMemo(
    () => buildShell(glasshouseFrameProfile(), FRAME_EXTRUDE, {
      depth: FRAME_EXTRUDE.depth,
      taper: GLASSHOUSE_TAPER,
      passes: 1
    }),
    []
  );
  return <group>
      {
    /* Underbody: also plugs the left-to-right tunnel the arch notches cut. */
  }
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[4.1, 0.56, 1.4]} />
        <meshPhysicalMaterial {...palette.chassis} />
      </mesh>

      <mesh geometry={body}>
        <meshPhysicalMaterial {...palette.body} />
      </mesh>

      <mesh geometry={glass}>
        <meshPhysicalMaterial {...GLASS} />
      </mesh>

      <mesh geometry={frame}>
        <meshPhysicalMaterial {...palette.secondary} />
      </mesh>

      {
    /* B-pillars divide the side glass; the frame only gives us A and C. */
  }
      {[-B_PILLAR_Z, B_PILLAR_Z].map((z) => <mesh key={`b-pillar-${z}`} position={[0.05, 1.16, z]} rotation={[0, 0, -0.06]}>
          <boxGeometry args={[0.075, 0.42, 0.035]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>)}

      {[-MIRROR_Z, MIRROR_Z].map((z) => <group key={`mirror-${z}`}>
          <mesh position={[1.1, 0.99, z * 0.945]}>
            <boxGeometry args={[0.05, 0.03, 0.11]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
          <mesh position={[1.06, 1, z]} rotation={[0, Math.sign(z) * 0.12, 0.05]}>
            <boxGeometry args={[0.08, 0.095, 0.18]} />
            <meshPhysicalMaterial {...palette.body} />
          </mesh>
        </group>)}

      {
    /*
      Lamps and grille sit proud of the shell on purpose. The nose surface curves
      away in both x and z, so anything flush with the profile ends up buried inside
      the bodywork and invisible.
    */
  }
      {[-0.46, 0.46].map((z) => <mesh key={`headlight-${z}`} position={[2.24, 0.71, z]} rotation={[0, 0, -0.14]}>
          <boxGeometry args={[0.17, 0.12, 0.38]} />
          <meshPhysicalMaterial {...HEADLIGHT} />
        </mesh>)}

      {
    /*
      The tail is heavily raked, so its surface sits much further forward than the
      rearmost point of the profile. Anything placed at the profile's extreme punches
      straight through as a sharp wedge.
    */
  }
      {[-0.52, 0.52].map((z) => <mesh key={`tail-light-${z}`} position={[-2.255, 0.78, z]}>
          <boxGeometry args={[0.075, 0.11, 0.34]} />
          <meshPhysicalMaterial {...TAIL_LIGHT} />
        </mesh>)}

      {
    /* grille, lower intake, rear bumper */
  }
      <mesh position={[2.29, 0.58, 0]}>
        <boxGeometry args={[0.12, 0.16, 0.76]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <mesh position={[2.28, 0.4, 0]}>
        <boxGeometry args={[0.1, 0.13, 1]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <mesh position={[-2.245, 0.4, 0]}>
        <boxGeometry args={[0.09, 0.14, 1.08]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>

      {[-1, 1].map((side) => <mesh key={`rocker-${side}`} position={[0, 0.32, side * 0.9]}>
          <boxGeometry args={[3.2, 0.06, 0.05]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>)}
      {[-1, 1].flatMap(
    (side) => [FRONT_AXLE_X, REAR_AXLE_X].map((x) => <mesh
      key={`arch-lip-${side}-${x}`}
      position={[x, 0.42, side * 0.92]}
    >
            <boxGeometry args={[0.55, 0.05, 0.04]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>)
  )}
      <mesh position={[-2.22, 0.78, 0]}>
        <boxGeometry args={[0.05, 0.08, 1.15]} />
        <meshPhysicalMaterial {...palette.accent} />
      </mesh>
    </group>;
}
export {
  CarBody
};
